import { createContext, useEffect, useState } from "react";
import { fetchConfigurazione } from "../lib/api/configurazione";
import { setConfig as setStoreConfig } from "../lib/config/store";

/**
 * Inietta nel <head> le variabili CSS tema arrivate dal DB (chiavi `cssvar.light.*`
 * e `cssvar.dark.*`), sovrascrivendo i default definiti in `styles/globals.css`.
 *
 * Traduce gli oggetti { "--var": "valore" } in due blocchi `:root { … }` (chiaro) e
 * `.dark { … }` (scuro) e li mette in un <style id="db-theme-overrides"> aggiunto in coda
 * all'<head>: essendo caricato dopo globals.css vince per ordine, senza toccarne il sorgente.
 */
function injectCssVars(cssvar) {
  if (!cssvar) return;
  const light = cssvar.light ?? {};
  const dark  = cssvar.dark  ?? {};
  const toDeclarations = (vars) =>
    Object.entries(vars).map(([k, v]) => `  ${k}: ${v};`).join("\n");
  const css = [
    `:root {\n${toDeclarations(light)}\n}`,
    `.dark {\n${toDeclarations(dark)}\n}`,
  ].join("\n");
  let el = document.getElementById("db-theme-overrides");
  if (!el) {
    el = document.createElement("style");
    el.id = "db-theme-overrides";
    document.head.appendChild(el);
  }
  el.textContent = css;
}

export const ConfigContext = createContext(null);

// Provider config: fetch dal DB all'avvio, valori esposti via useConfig().
// (soglie, tipi attività, stili, ecc.)
export function ConfigProvider({ children }) {
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    fetchConfigurazione()
      .then((data) => {
        if (data) {
          injectCssVars(data.cssvar);
          setStoreConfig(data);
          setConfig(data);
          setStatus("ready");
        } else {
          setStatus("error");
        }
      })
      .catch(() => setStatus("error"));
  }, []);

  if (status === "loading") {
    return (
      <div className="flex items-center justify-center min-h-screen text-ink-400 dark:text-ink-500">
        Caricamento...
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="flex items-center justify-center min-h-screen text-red-500">
        Configurazione non disponibile — ricarica la pagina.
      </div>
    );
  }

  return (
    <ConfigContext.Provider value={{ config }}>
      {children}
    </ConfigContext.Provider>
  );
}
